<template>
    <div class="feishu-bitable container h-100">
        <!--  左侧：表格管理侧栏  -->
        <div class="fbi-side">
            <div class="fbi-side-head">
                <span class="fbi-side-title">表格管理</span>
                <el-button type="primary" round size="mini" icon="el-icon-plus" @click="openAddBtn">新增</el-button>
            </div>
            <div class="fbi-table-list" v-loading="tableLoading">
                <div
                    v-for="item in tableList"
                    :key="item.id"
                    class="fbi-table-item"
                    :class="{active: selectedTableId === item.id}"
                    @click="selectTable(item.id)"
                >
                    <i class="el-icon-notebook-2 fbi-table-icon"></i>
                    <div class="fbi-table-info">
                        <div class="fbi-table-title">
                            {{ item.title }}
                            <span v-if="Number(item.is_default) === 1" class="fbi-default-badge">脚本默认</span>
                        </div>
                        <div class="fbi-table-meta">
                            <span class="fbi-mono" v-if="item.app_token" :title="item.app_token">
                                {{ item.app_token.length > 12 ? item.app_token.slice(0, 12) + '…' : item.app_token }}
                            </span>
                            <span v-else class="fbi-muted">未配置</span>
                        </div>
                    </div>
                    <div class="fbi-table-actions" @click.stop>
                        <i class="el-icon-edit-outline" title="编辑" @click="openEditBtn(item)"></i>
                        <i class="el-icon-delete fbi-icon-danger" title="删除" @click="deleteRow(item)"></i>
                    </div>
                </div>
                <div class="fbi-empty" v-if="!tableLoading && tableList.length === 0">
                    <i class="el-icon-folder-opened"></i>
                    <p>暂无表格</p>
                </div>
            </div>
        </div>
        <!--  右侧：推送记录主面板  -->
        <div class="fbi-main">
            <div class="fbi-main-head" v-loading="recordLoading">
                <div class="fbi-main-info">
                    <template v-if="selectedTable">
                        <div class="fbi-main-title">
                            {{ selectedTable.title }}
                            <a v-if="selectedTable.url" :href="selectedTable.url" target="_blank" class="fbi-open-link" title="在飞书打开">
                                <i class="el-icon-top-right"></i>
                            </a>
                        </div>
                        <div class="fbi-main-sub">
                            <span class="fbi-mono">{{ selectedTable.app_token }}</span>
                            <span class="fbi-sep">·</span>
                            <span class="fbi-mono">table: {{ selectedTable.table_id }}</span>
                            <template v-if="lastPush">
                                <span class="fbi-sep">·</span>
                                <span>共{{recordList.length||0}}张表，最后推送：{{ lastPush.pushed_at }}</span>
                                <el-tag size="mini" :type="lastPush.source === 2 ? 'success' : 'info'" effect="plain" round>
                                    {{ sourceText(lastPush.source) }}
                                </el-tag>
                            </template>
                        </div>
                    </template>
                    <div class="fbi-main-title fbi-muted" v-else>请选择左侧表格</div>
                </div>
                <div class="fbi-head-actions">
                    <el-button type="primary" size="mini" round plain icon="el-icon-video-play" @click="triggerAllPush"
                               :disabled="!selectedTableId" :loading="pushAllLoading">一键全量</el-button>
                    <el-button icon="el-icon-refresh" size="mini" round circle @click="getRecords"
                               :disabled="!selectedTableId" :loading="recordLoading"></el-button>
                </div>
            </div>
            <div class="fbi-record-body" v-loading="recordLoading">
                <el-table v-if="selectedTableId" :data="recordList" :row-style="{height: '52px'}"
                          :header-cell-style="{background: '#fafafa', color: '#606266', fontWeight: 600}"
                          class="fbi-record-table">
                    <el-table-column label="数据表" min-width="230">
                        <template slot-scope="scope">
                            <div class="fbi-module-name">{{ scope.row.module }}</div>
                        </template>
                    </el-table-column>
                    <el-table-column label="状态" align="center" width="100">
                        <template slot-scope="scope">
                            <el-tooltip v-if="scope.row.error_msg" :content="scope.row.error_msg" placement="top">
                                <span class="fbi-status" :class="'fbi-status-' + scope.row.status">
                                    <i class="fbi-status-dot"></i>{{ statusText(scope.row.status) }}
                                </span>
                            </el-tooltip>
                            <span v-else class="fbi-status" :class="'fbi-status-' + scope.row.status">
                                <i class="fbi-status-dot"></i>{{ statusText(scope.row.status) }}
                            </span>
                        </template>
                    </el-table-column>
                    <el-table-column label="进度" width="180">
                        <template slot-scope="scope">
                            <el-progress
                                v-if="scope.row.status !== 0"
                                class="fbi-progress"
                                :percentage="Number(scope.row.progress) || 0"
                                :stroke-width="10"
                                :status="scope.row.status === 3 ? 'exception' : (scope.row.status === 2 ? 'success' : undefined)"
                            ></el-progress>
                            <span v-else class="fbi-muted">-</span>
                        </template>
                    </el-table-column>
                    <el-table-column label="来源" align="center" width="80">
                        <template slot-scope="scope">
                            <el-tag v-if="scope.row.source" size="mini" :type="scope.row.source === 2 ? 'success' : 'info'"
                                    effect="plain" round>{{ sourceText(scope.row.source) }}</el-tag>
                            <span v-else class="fbi-muted">-</span>
                        </template>
                    </el-table-column>
                    <el-table-column label="上次推送" align="center" width="160">
                        <template slot-scope="scope">
                            <span :class="scope.row.pushed_at ? '' : 'fbi-muted'">{{ scope.row.pushed_at || '-' }}</span>
                        </template>
                    </el-table-column>
                    <el-table-column label="模式" align="center" width="100">
                        <template slot-scope="scope">{{ modeText(scope.row.mode) }}</template>
                    </el-table-column>
                    <el-table-column label="数量" align="center" width="80">
                        <template slot-scope="scope">
                            <span :class="scope.row.count != null ? 'fbi-mono' : 'fbi-muted'">
                                {{ scope.row.count != null ? scope.row.count : '-' }}
                            </span>
                        </template>
                    </el-table-column>
                    <el-table-column label="耗时(m)" align="center" width="110">
                        <template slot-scope="scope">
                            <span :class="scope.row.cost_time != null ? 'fbi-mono' : 'fbi-muted'">
                                {{ scope.row.cost_time != null ? (scope.row.cost_time / 60000).toFixed(2) : '-' }}
                            </span>
                        </template>
                    </el-table-column>
                    <el-table-column label="操作" align="center" width="200">
                        <template slot-scope="scope">
                            <el-button type="primary" size="mini" round :loading="pushingModule === scope.row.module"
                                       @click="triggerPush(scope.row, 3)">全量推送</el-button>
                        </template>
                    </el-table-column>
                </el-table>
                <div class="fbi-empty fbi-empty-main" v-else>
                    <i class="el-icon-data-board"></i>
                    <p>选择表格后查看各数据表的推送状态</p>
                </div>
            </div>
        </div>

        <!--  新增 / 编辑 弹窗  -->
        <el-dialog :title="form.id ? '编辑表格' : '新增表格'" :visible.sync="dialogVisible" width="560px" append-to-body>
            <el-form ref="form" :model="form" :rules="formRules" label-width="100px" size="small">
                <el-form-item label="完整链接" prop="url">
                    <el-input v-model="form.url" type="textarea" :rows="2"
                              placeholder="粘贴飞书多维表格完整链接，将自动提取 App Token / 数据表ID"
                              @input="extractFromUrl"></el-input>
                </el-form-item>
                <el-form-item label="App Token" prop="app_token">
                    <el-input v-model="form.app_token" placeholder="链接自动提取，或手工填写"></el-input>
                </el-form-item>
                <el-form-item label="数据表ID" prop="table_id">
                    <el-input v-model="form.table_id" placeholder="链接自动提取，或手工填写"></el-input>
                </el-form-item>
                <el-form-item label="名称" prop="title">
                    <el-input v-model="form.title" placeholder="自定义名称（必填，不可重复）"></el-input>
                </el-form-item>
                <el-form-item label="脚本默认">
                    <el-switch v-model="form.is_default" active-color="#ff3176"></el-switch>
                    <span class="fbi-form-tip">设为「脚本默认推送」后，每日定时脚本将默认推送到此表格（全局仅一条）</span>
                </el-form-item>
            </el-form>
            <div slot="footer">
                <el-button size="small" @click="dialogVisible = false">取 消</el-button>
                <el-button type="primary" size="small" :loading="saving" @click="saveForm">保 存</el-button>
            </div>
        </el-dialog>
    </div>
</template>

<script>
import {
    feishuPushTables,
    saveFeishuPushTable,
    deleteFeishuPushTable,
    feishuPushModules,
    feishuPushRecords,
    feishuPushTrigger
} from '@/api/feishu'

export default {
    name: 'feishuBitable',
    data() {
        return {
            // 表格管理
            tableList: [],
            tableLoading: false,
            // 新增 / 编辑
            dialogVisible: false,
            saving: false,
            form: {id: 0, url: '', app_token: '', table_id: '', title: '', is_default: false},
            formRules: {
                app_token: [{required: true, message: 'App Token 不能为空', trigger: 'blur'}],
                table_id: [{required: true, message: '数据表ID 不能为空', trigger: 'blur'}],
                title: [{required: true, message: '名称不能为空', trigger: 'blur'}]
            },
            // 推送记录
            modules: [],
            selectedTableId: null,
            recordList: [],
            recordLoading: false,
            pushingModule: '',
            pushAllLoading: false
        }
    },
    computed: {
        selectedTable() {
            return this.tableList.find(item => item.id === this.selectedTableId) || null
        },
        // 当前表格最近一次推送（取各数据表推送记录中 pushed_at 最新的一条，用于头部展示最后更新时间及来源）
        lastPush() {
            let best = null
            this.recordList.forEach(item => {
                if (!item.pushed_at) {
                    return
                }
                if (!best || item.pushed_at > best.pushed_at) {
                    best = item
                }
            })
            return best
        }
    },
    methods: {
        // 表格管理列表
        getTableList() {
            this.tableLoading = true
            feishuPushTables({page_size: 100, current_page: 1}).then(res => {
                const data = res.data || {}
                this.tableList = data.list || []
                // 默认选中第一个表格
                if (this.tableList.length > 0 && !this.selectedTableId) {
                    this.selectedTableId = this.tableList[0].id
                    this.getRecords()
                }
            }).finally(() => {
                this.tableLoading = false
            })
        },
        // 侧栏选中表格
        selectTable(id) {
            if (this.selectedTableId === id) {
                return
            }
            this.selectedTableId = id
            this.getRecords()
        },
        // 获取推送记录（与模块清单合并，未推送的模块默认「未推送」）
        getRecords() {
            if (!this.selectedTableId) {
                this.recordList = []
                return
            }
            this.recordLoading = true
            feishuPushRecords({push_table_id: this.selectedTableId}).then(res => {
                const records = (res.data && res.data.list) || []
                const recordMap = {}
                records.forEach(item => {
                    recordMap[item.module] = item
                })
                this.recordList = this.modules.map(item => {
                    const record = recordMap[item.name] || {}
                    return {
                        module: item.name,
                        status: record.status != null ? record.status : 0,
                        progress: record.progress != null ? Number(record.progress) : (record.status == 2 ? 100 : 0),
                        source: record.source || 0,
                        mode: record.mode || 1,
                        count: record.count != null ? record.count : null,
                        cost_time: record.cost_time != null ? record.cost_time : null,
                        pushed_at: record.pushed_at || null,
                        error_msg: record.error_msg || ''
                    }
                })
            }).finally(() => {
                this.recordLoading = false
            })
        },
        // 新增
        openAddBtn() {
            this.form = {id: 0, url: '', app_token: '', table_id: '', title: '', is_default: false}
            this.dialogVisible = true
            this.$nextTick(() => {
                this.$refs.form && this.$refs.form.clearValidate()
            })
        },
        // 编辑
        openEditBtn(row) {
            this.form = {
                id: row.id,
                url: row.url || '',
                app_token: row.app_token || '',
                table_id: row.table_id || '',
                title: row.title || '',
                is_default: Number(row.is_default) === 1
            }
            this.dialogVisible = true
            this.$nextTick(() => {
                this.$refs.form && this.$refs.form.clearValidate()
            })
        },
        // 从完整链接中提取 App Token / 数据表ID
        extractFromUrl(url) {
            const appToken = /\/base\/([A-Za-z0-9]+)/.exec(url || '')
            const tableId = /[?&]table=([A-Za-z0-9]+)/.exec(url || '')
            if (appToken) {
                this.form.app_token = appToken[1]
            }
            if (tableId) {
                this.form.table_id = tableId[1]
            }
        },
        // 保存（新增 / 编辑）
        saveForm() {
            this.$refs.form.validate(valid => {
                if (!valid) {
                    return
                }
                this.saving = true
                saveFeishuPushTable({
                    ...this.form,
                    is_default: this.form.is_default ? 1 : 0
                }).then(res => {
                    if (res && res.success === false) {
                        this.$message.error(res.message || '保存失败')
                        return
                    }
                    this.$message.success('保存成功')
                    this.dialogVisible = false
                    this.getTableList()
                }).finally(() => {
                    this.saving = false
                })
            })
        },
        // 删除
        deleteRow(row) {
            this.$confirm(`确定要删除表格【${row.title}】吗？其下推送记录将一并删除。`, '提示', {
                confirmButtonText: '确定',
                cancelButtonText: '取消',
                type: 'warning'
            }).then(() => {
                return deleteFeishuPushTable(row.id)
            }).then(res => {
                if (res && res.success === false) {
                    this.$message.error(res.message || '删除失败')
                    return
                }
                this.$message.success('删除成功')
                if (this.selectedTableId === row.id) {
                    this.selectedTableId = null
                    this.recordList = []
                }
                this.getTableList()
            }).catch(() => {
            })
        },
        // 触发推送（后台异步执行，状态/进度列实时展示）
        triggerPush(row, mode) {
            this.pushingModule = row.module
            feishuPushTrigger({push_table_id: this.selectedTableId, module: row.module, mode}).then(res => {
                if (res && res.success === false) {
                    this.$message.error(res.message || '推送失败')
                    return
                }
                this.$message.success(res.message || '推送已触发')
                this.getRecords()
            }).catch(err => {
                this.$message.error((err && err.message) || '推送失败')
            }).finally(() => {
                this.pushingModule = ''
            })
        },
        // 一键全量：单次请求（module='all'）触发当前表格全部模块的全量推送
        // 后端只投递一个队列事件，避免并发请求撞上「同表格已有推送任务执行中」的防重校验
        triggerAllPush() {
            if (!this.selectedTableId) {
                this.$message.warning('请先选择左侧表格')
                return
            }
            if (!this.modules.length) {
                this.$message.warning('暂无可用模块')
                return
            }
            this.$confirm(
                `确定要对【${this.selectedTable ? this.selectedTable.title : ''}】的全部 ${this.modules.length} 个模块执行全量推送吗？此操作将重建各数据表并重推全量数据，耗时可能较长。`,
                '提示',
                {
                    confirmButtonText: '确定',
                    cancelButtonText: '取消',
                    type: 'warning'
                }
            ).then(() => {
                this.pushAllLoading = true
                return feishuPushTrigger({
                    push_table_id: this.selectedTableId,
                    module: 'all',
                    mode: 3
                })
            }).then(res => {
                if (res && res.success === false) {
                    this.$message.error(res.message || '触发失败')
                    return
                }
                this.$message.success('已触发全部模块全量推送')
                this.getRecords()
            }).catch(err => {
                if (!err || err === 'cancel' || err === 'close') {
                    return
                }
                this.$message.error((err && err.message) || '一键全量触发失败，请重试')
            }).finally(() => {
                this.pushAllLoading = false
            })
        },
        // 状态 tag 颜色
        statusTagType(status) {
            const s = Number(status)
            if (s === 2) {
                return 'success'
            }
            if (s === 1) {
                return 'warning'
            }
            if (s === 3) {
                return 'danger'
            }
            return 'info'
        },
        statusText(status) {
            const map = {0: '未推送', 1: '推送中', 2: '成功', 3: '失败'}
            return map[Number(status)] || '未推送'
        },
        sourceText(source) {
            const map = {1: '手动', 2: '自动'}
            return map[Number(source)] || '-'
        },
        modeText(mode) {
            const map = {1: '全量', 2: '增量', 3: '分段全量'}
            return map[Number(mode)] || '-'
        }
    },
    created() {
        feishuPushModules().then(res => {
            const data = res.data || []
            this.modules = Array.isArray(data) ? data : (data.list || [])
        })
        this.getTableList()
    }
}
</script>

<style scoped>
.feishu-bitable {
    display: flex;
    align-items: stretch;
    background: transparent;
    min-height: 100%;
}
/*  ----------  左侧：表格管理侧栏  ---------- */
.feishu-bitable .fbi-side {
    flex: 0 0 300px;
    width: 300px;
    background: #fff;
    border-radius: 10px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
    display: flex;
    flex-direction: column;
    overflow: hidden;
}
.feishu-bitable .fbi-side-head {
    padding: 14px 16px;
    border-bottom: 1px solid #f2f3f5;
    display: flex;
    align-items: center;
    justify-content: space-between;
}
.feishu-bitable .fbi-side-title {
    font-size: 15px;
    font-weight: 600;
    color: #303133;
    padding-left: 10px;
    position: relative;
}
.feishu-bitable .fbi-side-title::before {
    content: '';
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 4px;
    height: 15px;
    border-radius: 2px;
    background: #ff3176;
}
.feishu-bitable .fbi-table-list {
    flex: 1;
    overflow-y: auto;
    padding: 10px;
}
.feishu-bitable .fbi-table-item {
    display: flex;
    align-items: center;
    padding: 10px 12px;
    margin-bottom: 8px;
    border: 1px solid #ebedf0;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s;
}
.feishu-bitable .fbi-table-item:hover {
    border-color: rgba(255, 49, 118, 0.4);
    box-shadow: 0 2px 8px rgba(255, 49, 118, 0.12);
}
.feishu-bitable .fbi-table-item.active {
    border-color: #ff3176;
    background: rgba(255, 49, 118, 0.05);
}
.feishu-bitable .fbi-table-icon {
    flex: 0 0 auto;
    font-size: 22px;
    color: #c0c4cc;
    margin-right: 10px;
    transition: color 0.2s;
}
.feishu-bitable .fbi-table-item.active .fbi-table-icon {
    color: #ff3176;
}
.feishu-bitable .fbi-table-info {
    flex: 1;
    min-width: 0;
}
.feishu-bitable .fbi-table-title {
    font-size: 13px;
    font-weight: 500;
    color: #303133;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
.feishu-bitable .fbi-table-title .fbi-default-badge {
    display: inline-block;
    margin-left: 6px;
    padding: 1px 6px;
    font-size: 11px;
    font-weight: 400;
    line-height: 1.6;
    color: #fff;
    background: #ff3176;
    border-radius: 8px;
    vertical-align: 1px;
}
.feishu-bitable .fbi-form-tip {
    margin-left: 10px;
    font-size: 12px;
    color: #909399;
}
.feishu-bitable .fbi-table-item.active .fbi-table-title {
    color: #ff3176;
}
.feishu-bitable .fbi-table-meta {
    margin-top: 3px;
    font-size: 12px;
    color: #909399;
}
.feishu-bitable .fbi-table-actions {
    flex: 0 0 auto;
    display: none;
    margin-left: 8px;
}
.feishu-bitable .fbi-table-item:hover .fbi-table-actions {
    display: block;
}
.feishu-bitable .fbi-table-actions i {
    font-size: 15px;
    color: #909399;
    margin-left: 8px;
    cursor: pointer;
    transition: color 0.2s;
}
.feishu-bitable .fbi-table-actions i:hover {
    color: #ff3176;
}
.feishu-bitable .fbi-table-actions .fbi-icon-danger:hover {
    color: #f56c6c;
}
/*  ----------  右侧：推送记录主面板  ---------- */
.feishu-bitable .fbi-main {
    flex: 1;
    min-width: 0;
    margin-left: 12px;
    background: #fff;
    border-radius: 10px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
    display: flex;
    flex-direction: column;
    overflow: hidden;
}
.feishu-bitable .fbi-main-head {
    padding: 16px 20px;
    border-bottom: 1px solid #f2f3f5;
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: linear-gradient(90deg, rgba(255, 49, 118, 0.06) 0%, rgba(255, 49, 118, 0) 40%);
}
.feishu-bitable .fbi-main-title {
    font-size: 16px;
    font-weight: 600;
    color: #303133;
    display: flex;
    align-items: center;
}
.feishu-bitable .fbi-head-actions {
    display: flex;
    align-items: center;
    gap: 8px;
}
.feishu-bitable .fbi-open-link {
    margin-left: 8px;
    font-size: 14px;
    color: #ff3176;
}
.feishu-bitable .fbi-open-link:hover {
    opacity: 0.8;
}
.feishu-bitable .fbi-main-sub {
    margin-top: 5px;
    font-size: 12px;
    color: #909399;
}
.feishu-bitable .fbi-sep {
    margin: 0 6px;
    color: #dcdfe6;
}
.feishu-bitable .fbi-main-sub .el-tag {
    margin-left: 8px;
    vertical-align: 1px;
}
.feishu-bitable .fbi-record-body {
    flex: 1;
    overflow: auto;
    padding: 12px 20px 20px;
}
.feishu-bitable .fbi-record-table {
    border-radius: 8px;
}
.feishu-bitable .fbi-module-name {
    font-size: 13px;
    font-weight: 500;
    color: #303133;
}
/*  状态指示  */
.feishu-bitable .fbi-status {
    display: inline-flex;
    align-items: center;
    font-size: 13px;
}
.feishu-bitable .fbi-status-dot {
    display: inline-block;
    width: 7px;
    height: 7px;
    border-radius: 50%;
    margin-right: 6px;
    background: #c0c4cc;
}
.feishu-bitable .fbi-status-0 {
    color: #909399;
}
.feishu-bitable .fbi-status-1 {
    color: #e6a23c;
}
.feishu-bitable .fbi-status-1 .fbi-status-dot {
    background: #e6a23c;
    animation: fbi-pulse 1.2s infinite;
}
.feishu-bitable .fbi-status-2 {
    color: #67c23a;
}
.feishu-bitable .fbi-status-2 .fbi-status-dot {
    background: #67c23a;
}
.feishu-bitable .fbi-status-3 {
    color: #f56c6c;
}
.feishu-bitable .fbi-status-3 .fbi-status-dot {
    background: #f56c6c;
}
.feishu-bitable .fbi-progress {
    display: inline-block;
    width: 140px;
    vertical-align: middle;
}
@keyframes fbi-pulse {
    0% {
        box-shadow: 0 0 0 0 rgba(230, 162, 60, 0.5);
    }
    70% {
        box-shadow: 0 0 0 5px rgba(230, 162, 60, 0);
    }
    100% {
        box-shadow: 0 0 0 0 rgba(230, 162, 60, 0);
    }
}
/*  通用辅助  */
.feishu-bitable .fbi-mono {
    font-family: Menlo, Consolas, monospace;
    font-size: 12px;
}
.feishu-bitable .fbi-muted {
    color: #c0c4cc;
}
.feishu-bitable .fbi-empty {
    text-align: center;
    padding: 40px 0;
    color: #c0c4cc;
}
.feishu-bitable .fbi-empty i {
    font-size: 40px;
}
.feishu-bitable .fbi-empty p {
    margin-top: 10px;
    font-size: 13px;
}
.feishu-bitable .fbi-empty-main {
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 320px;
}
</style>
